import { Dispatch, SetStateAction, useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  DialogTitle,
  Stack,
  FormLabel,
} from '@mui/material';
import { sortByUnderscoreNumber } from '@src/utils/sortByUnderscoreNumber';
import storeConnector from '@src/store/storeConnector';
import createDeviceDialogSx from '../../../_shared/sxStyles/Device/CreateDeviceDialogSx';
import { CreateDeviceDto } from '@src/types/Device';
import useGetControllers from '@src/hooks/api/queries/devices/useGetControllers';
import useGetParentDevices from '@hooks/api/queries/collections/useGetDevicesParents';
import useGetDeviceTypes from '@hooks/api/queries/collections/useGetDeviceTypes';
import useCreateDevice from '@src/hooks/api/mutations/useCreateDevice';

interface DialogProps {
  setOpen: Dispatch<SetStateAction<boolean>>;
  setConfirmOpen: Dispatch<SetStateAction<boolean>>;
  username?: string;
}

function CreateDeviceDialog({ setOpen, setConfirmOpen }: DialogProps) {
  const [selectedParentDeviceType, setSelectedParentDeviceType] = useState<
    string | null
  >(null);
  const [deviceTypes, setDeviceTypes] = useState<
    { name: string; parentSourceDeviceNull: boolean }[]
  >([]);
  const [hideParentSourceDevice, setHideParentSourceDevice] = useState(false);
  const [hideParentController, setHideParentController] = useState(false);
  const [
    showParentControllerInsteadOfSource,
    setShowParentControllerInsteadOfSource,
  ] = useState(false);
  const [showBothParentsAndControllers, setShowBothParentsAndControllers] =
    useState(false);
  const [isManuallyCleared, setIsManuallyCleared] = useState(false);

  const { control, handleSubmit, watch, setValue, reset } =
    useForm<CreateDeviceDto>({
      defaultValues: {
        name: '',
        engineeringId: null,
        deviceId: '',
        type: '',
        parentControllerId: '',
        parentSourceDeviceId: '',
        isSiteControllerSourceDevice: false,
      },
      mode: 'onChange',
    });

  const watchedValues = watch();

  const onSubmit = (data: CreateDeviceDto) => {
    // Check if any controller has type 'siteController'
    const hasSiteController =
      parentControllers?.devices?.some(
        (device) => device.type === 'siteController',
      ) || false;

    // Convert empty strings to null for all string fields except deviceId
    const sanitizedData = {
      ...data,
      name: data.name === '' ? null : data.name,
      engineeringId: data.engineeringId === '' ? null : data.engineeringId,
      // deviceId: keep as empty string, don't convert to null
      type: data.type === '' ? null : data.type,
      parentControllerId:
        data.parentControllerId === '' ? null : data.parentControllerId,
      parentSourceDeviceId:
        data.parentSourceDeviceId === '' ? null : data.parentSourceDeviceId,
      isSiteControllerSourceDevice: hasSiteController,
    } as CreateDeviceDto;

    createDevice(sanitizedData);
    setConfirmOpen(true);
    setOpen(false);
  };

  const handleClear = () => {
    setSelectedParentDeviceType('');
    setHideParentSourceDevice(false);
    setHideParentController(false);
    setShowParentControllerInsteadOfSource(false);
    setShowBothParentsAndControllers(false);
    setIsManuallyCleared(true);
    reset();
  };

  const handleCancel = () => {
    setSelectedParentDeviceType('');
    setHideParentSourceDevice(false);
    setHideParentController(false);
    setShowParentControllerInsteadOfSource(false);
    setShowBothParentsAndControllers(false);
    setIsManuallyCleared(true);
    reset();
    setOpen(false);
  };

  const { data: deviceTypesData } = useGetDeviceTypes();
  const { data: parentDevices } = useGetParentDevices({
    deviceType: watchedValues.type,
    options: {
      enabled: watchedValues.type !== '' && watchedValues.type !== undefined,
    },
    queryKeys: [watchedValues.type],
  });
  const { data: parentControllers } = useGetControllers(
    selectedParentDeviceType || watchedValues.type || '',
  );
  const { createDevice: createDeviceMutation } = useCreateDevice();

  const createDevice = (formData: CreateDeviceDto) => {
    createDeviceMutation.mutate(formData);
  };

  useEffect(() => {
    if (deviceTypesData?.deviceTypes) {
      setDeviceTypes(deviceTypesData.deviceTypes);
    }
  }, [deviceTypesData]);

  // Hide Parent Controller dropdown if siteController or unitController device is found
  useEffect(() => {
    if (parentDevices?.devices && Array.isArray(parentDevices.devices)) {
      const hasControllerDevice = parentDevices.devices.some(
        (device) =>
          device.type === 'siteController' || device.type === 'unitController',
      );
      setHideParentController(hasControllerDevice);
    } else {
      setHideParentController(false);
    }
  }, [parentDevices]);

  // Show Parent Controller dropdown instead of Parent Source Device if controllers data exists
  // Show both dropdowns if controllers and parents have different device types
  useEffect(() => {
    const hasControllers =
      parentControllers?.devices &&
      Array.isArray(parentControllers.devices) &&
      parentControllers.devices.length > 0;
    const hasParents =
      parentDevices?.devices &&
      Array.isArray(parentDevices.devices) &&
      parentDevices.devices.length > 0;

    if (hasControllers && hasParents) {
      // Get unique device types from both sources
      const controllerTypes = new Set(
        parentControllers.devices.map((device) => device.type).filter(Boolean),
      );
      const parentTypes = new Set(
        parentDevices.devices.map((device) => device.type).filter(Boolean),
      );

      // Check if there are different types between the two sets
      const hasDifferentTypes =
        Array.from(controllerTypes).some((type) => !parentTypes.has(type)) ||
        Array.from(parentTypes).some((type) => !controllerTypes.has(type));

      if (hasDifferentTypes) {
        setShowBothParentsAndControllers(true);
        setShowParentControllerInsteadOfSource(false);
      } else {
        setShowBothParentsAndControllers(false);
        setShowParentControllerInsteadOfSource(true);
      }
    } else if (hasControllers) {
      setShowBothParentsAndControllers(false);
      setShowParentControllerInsteadOfSource(true);
    } else {
      setShowBothParentsAndControllers(false);
      setShowParentControllerInsteadOfSource(false);
    }
  }, [parentControllers, parentDevices]);

  // Auto-select Parent Source Device if only one option is available
  useEffect(() => {
    if (
      !hideParentSourceDevice &&
      parentDevices?.devices &&
      Array.isArray(parentDevices.devices) &&
      parentDevices.devices.length === 1 &&
      !watchedValues.parentSourceDeviceId &&
      !isManuallyCleared
    ) {
      const singleDevice = parentDevices.devices[0];
      setValue('parentSourceDeviceId', singleDevice.deviceId);
    }
  }, [
    parentDevices,
    hideParentSourceDevice,
    watchedValues.parentSourceDeviceId,
    isManuallyCleared,
    setValue,
  ]);

  // Auto-select Parent Controller if only one option is available
  useEffect(() => {
    const controllerDevices = hideParentSourceDevice
      ? parentDevices?.devices
      : parentControllers?.devices;

    if (
      controllerDevices &&
      Array.isArray(controllerDevices) &&
      controllerDevices.length === 1 &&
      !watchedValues.parentControllerId &&
      !isManuallyCleared
    ) {
      const singleController = controllerDevices[0];
      setValue('parentControllerId', singleController.deviceId);
    }
  }, [
    parentControllers,
    parentDevices,
    hideParentSourceDevice,
    watchedValues.parentControllerId,
    isManuallyCleared,
    setValue,
  ]);

  const handleDeviceTypeChange = (selectedType: string) => {
    setValue('type', selectedType);

    const foundType = deviceTypes.find((type) => type.name === selectedType);

    if (foundType) {
      setHideParentSourceDevice(foundType.parentSourceDeviceNull);
    }

    // Clear parent device selections when device type changes
    if (watchedValues.parentSourceDeviceId) {
      setValue('parentSourceDeviceId', '');
    }

    if (watchedValues.parentControllerId) {
      setValue('parentControllerId', '');
    }

    // Reset manually cleared flag when user makes new selections
    setIsManuallyCleared(false);
  };

  const isCreateButtonEnabled =
    watchedValues.type &&
    (showBothParentsAndControllers
      ? watchedValues.parentSourceDeviceId && watchedValues.parentControllerId
      : showParentControllerInsteadOfSource
        ? watchedValues.parentControllerId
        : watchedValues.parentSourceDeviceId) &&
    (hideParentController || watchedValues.parentControllerId) &&
    watchedValues.name &&
    watchedValues.engineeringId;

  return (
    <Dialog
      open
      fullWidth
      maxWidth='sm'
      PaperProps={{
        sx: createDeviceDialogSx.dialogPaper,
      }}
    >
      <DialogTitle>Create Device</DialogTitle>

      <DialogContent sx={createDeviceDialogSx.dialogContent}>
        <Stack gap={2} component='form' onSubmit={handleSubmit(onSubmit)}>
          <div>
            <FormLabel sx={createDeviceDialogSx.fieldLabel}>
              Device Type
            </FormLabel>

            <Controller
              name='type'
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    handleDeviceTypeChange(e.target.value);
                  }}
                  fullWidth
                  size='small'
                  sx={createDeviceDialogSx.deviceTypeField}
                >
                  {(() => {
                    if (
                      !Array.isArray(deviceTypes) ||
                      deviceTypes.length === 0
                    ) {
                      return (
                        <MenuItem value=''>No device types available</MenuItem>
                      );
                    }

                    const filteredTypes = deviceTypes.filter(
                      (type) =>
                        type &&
                        typeof type.name === 'string' &&
                        type.name.trim() !== '' &&
                        type.name !== 'siteController',
                    );

                    if (filteredTypes.length === 0) {
                      return (
                        <MenuItem value=''>No device types available</MenuItem>
                      );
                    }

                    return filteredTypes.map((type) => (
                      <MenuItem key={type.name} value={type.name}>
                        {type.name}
                      </MenuItem>
                    ));
                  })()}
                </TextField>
              )}
            />
          </div>

          {(showBothParentsAndControllers ||
            !showParentControllerInsteadOfSource) && (
            <div>
              <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                Parent Source Device
              </FormLabel>

              <Controller
                name='parentSourceDeviceId'
                control={control}
                render={({ field }) => (
                  <TextField
                    select
                    {...field}
                    value={field.value || ''}
                    fullWidth
                    size='small'
                    sx={createDeviceDialogSx.parentDeviceField}
                    disabled={!watchedValues.type}
                  >
                    <MenuItem value=''>
                      {!watchedValues.type
                        ? 'Select device type first'
                        : !parentDevices?.devices?.length
                          ? 'Loading parent devices...'
                          : 'Select a device'}
                    </MenuItem>

                    {parentDevices &&
                      parentDevices.devices &&
                      Array.isArray(parentDevices.devices) &&
                      sortByUnderscoreNumber(
                        parentDevices.devices.map((device) => device.deviceId),
                      ).map((deviceId: string) => (
                        <MenuItem key={deviceId} value={deviceId}>
                          {deviceId}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
              />
            </div>
          )}

          {(showBothParentsAndControllers ||
            showParentControllerInsteadOfSource ||
            !hideParentController) && (
            <div>
              <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                Parent Controller
              </FormLabel>

              <Controller
                name='parentControllerId'
                control={control}
                render={({ field }) => (
                  <TextField
                    select
                    {...field}
                    value={field.value || ''}
                    fullWidth
                    size='small'
                    sx={createDeviceDialogSx.parentDeviceField}
                    disabled={
                      !watchedValues.type ||
                      (!showParentControllerInsteadOfSource &&
                        !hideParentSourceDevice &&
                        !watchedValues.parentSourceDeviceId)
                    }
                  >
                    <MenuItem value=''>
                      {!watchedValues.type
                        ? 'Select device type first'
                        : !hideParentSourceDevice &&
                            !watchedValues.parentSourceDeviceId
                          ? 'Select parent source device first'
                          : hideParentSourceDevice &&
                              !parentDevices?.devices?.length
                            ? 'Loading parent controllers...'
                            : !hideParentSourceDevice &&
                                !parentControllers?.devices?.length
                              ? 'Loading parent controllers...'
                              : 'Select a controller'}
                    </MenuItem>

                    {(showBothParentsAndControllers ||
                      showParentControllerInsteadOfSource) &&
                      parentControllers &&
                      parentControllers.devices &&
                      Array.isArray(parentControllers.devices) &&
                      sortByUnderscoreNumber(
                        parentControllers.devices.map(
                          (device: { deviceId: string; type: string }) =>
                            device.deviceId,
                        ),
                      ).map((deviceId: string) => (
                        <MenuItem key={deviceId} value={deviceId}>
                          {deviceId}
                        </MenuItem>
                      ))}

                    {!showBothParentsAndControllers &&
                      !showParentControllerInsteadOfSource &&
                      hideParentSourceDevice &&
                      parentDevices &&
                      parentDevices.devices &&
                      Array.isArray(parentDevices.devices) &&
                      sortByUnderscoreNumber(
                        parentDevices.devices.map((device) => device.deviceId),
                      ).map((deviceId: string) => (
                        <MenuItem key={deviceId} value={deviceId}>
                          {deviceId}
                        </MenuItem>
                      ))}

                    {!showBothParentsAndControllers &&
                      !showParentControllerInsteadOfSource &&
                      !hideParentSourceDevice &&
                      parentControllers &&
                      parentControllers.devices &&
                      Array.isArray(parentControllers.devices) &&
                      sortByUnderscoreNumber(
                        parentControllers.devices.map(
                          (device: { deviceId: string; type: string }) =>
                            device.deviceId,
                        ),
                      ).map((deviceId: string) => (
                        <MenuItem key={deviceId} value={deviceId}>
                          {deviceId}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
              />
            </div>
          )}

          <div>
            <FormLabel sx={createDeviceDialogSx.fieldLabel}>
              Device Name
            </FormLabel>

            <Controller
              name='name'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size='small'
                  sx={createDeviceDialogSx.textField}
                />
              )}
            />
          </div>

          <div>
            <FormLabel sx={createDeviceDialogSx.fieldLabel}>
              Engineering ID
            </FormLabel>

            <Controller
              name='engineeringId'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value === '' ? null : value);
                  }}
                  fullWidth
                  size='small'
                  sx={createDeviceDialogSx.textField}
                />
              )}
            />
          </div>
        </Stack>
      </DialogContent>

      <DialogActions sx={createDeviceDialogSx.dialogActions}>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleClear}>Clear</Button>
        <Button
          variant='contained'
          type='submit'
          onClick={handleSubmit(onSubmit)}
          disabled={!isCreateButtonEnabled}
        >
          Create Device
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default storeConnector(CreateDeviceDialog, {
  user: ['role', 'username'],
});
